/**
 * Tipos y textos del catalogo de conceptos (`/conceptos`, `/ajustes`, `/orden`).
 *
 * Escritos a mano contra el servidor (`conceptos/modelo.py`: `Concepto.to_dict`,
 * `ConceptoResuelto.to_dict`, `Ajuste.to_dict`, `PasoOrden.to_dict`; y
 * `servicio.py`: `resumen_conceptos`, `configuracion_concepto`). Los nombres de
 * campo son los de la API (snake_case).
 *
 * Aqui tambien viven los textos de las preguntas en lenguaje sencillo (`REGLAS`),
 * los nombres de las etapas del calculo (`ETAPAS`) y las funciones puras que
 * los usan. No hay reglas de negocio: herencia, comparacion con la ley,
 * validacion y orden de calculo los da la API.
 */
import type { LimiteLft } from '@schemas/fijas';
import type { Alcance, RegistroHistorial } from './catalogo';

// ── Vocabulario ─────────────────────────────────────────────────────────────

export type TipoConcepto = 'PERCEPCION' | 'DEDUCCION';
export type Afectacion = 'DIRECTA' | 'INDIRECTA' | 'COMPLEMENTARIA';
export type EstadoConcepto = 'ACTIVO' | 'NO_USADO';
/** BASE: catalogo base (claves 1 a 999); PROPIO: dado de alta o clonado. */
export type OrigenConcepto = 'BASE' | 'PROPIO';
export type ModoCalculo = 'FORMULA' | 'CAPTURA' | 'SISTEMA';
export type TipoRegla = 'CALCULO' | 'EXENCION_ISR' | 'METODO_ISR' | 'INTEGRACION_SBC' | 'BASE_ISN';
export type TipoReglaFiscal = Exclude<TipoRegla, 'CALCULO'>;
/** LEY: con fundamento; CLIENTE: convencion o politica; DERIVADA: inferida, por confirmar. */
export type Etiqueta = 'LEY' | 'CLIENTE' | 'DERIVADA';
export type ClaseNomina = 'ORDINARIA' | 'EXTRAORDINARIA' | 'AGUINALDO' | 'FINIQUITO' | 'COMPLEMENTARIA';
export type TipoCampoCaptura = 'NUMERO' | 'PORCENTAJE' | 'TEXTO' | 'OPCION' | 'BASE';
/** En que periodos del calendario se aplica el concepto. */
export type PeriodosAplica = 'TODOS' | 'PRIMERO_DEL_MES' | 'ULTIMO_DEL_MES';
/** Como se puede registrar el concepto a cada trabajador. */
export type FormaRegistro = 'UNA_VEZ' | 'MONTO_HASTA_TOTAL' | 'POR_FECHAS';
/** De que nivel viene un valor (`Traza.nivel`, `CriterioAplicado.nivel`). */
export type NivelAjuste = 'GENERAL' | 'ESQUEMA' | 'EMPRESA' | 'CLIENTE' | 'CLIENTE_EMPRESA' | 'TIPO_NOMINA';

// ── Ficha (configuracion general) ───────────────────────────────────────────

export interface Criterio {
  readonly id: string;
  readonly nombre: string;
  readonly es_ley: boolean;
  readonly formula: string | null;
  readonly valor: string | null;
  readonly fundamento: string;
  readonly etiqueta: Etiqueta | null;
}

export interface Regla {
  readonly tipo: TipoRegla;
  readonly criterios: readonly Criterio[];
  readonly criterio_default: string;
  /** Excepciones del ISN: estado (AGS, CDMX…) → 'SI' | 'NO'. */
  readonly por_estado: Readonly<Record<string, string>>;
}

export interface DatoCaptura {
  readonly nombre: string;
  readonly tipo: TipoCampoCaptura;
  readonly opciones: readonly string[] | null;
  readonly requerido: boolean;
  readonly descripcion: string;
}

/** `GET /conceptos/{clave}` y la `ficha` de la configuracion. Es lo que viaja en `PUT /conceptos/{clave}`. */
export interface ConceptoFicha {
  readonly clave: number;
  readonly nombre: string;
  readonly nombre_corto: string;
  readonly tipo: TipoConcepto;
  readonly afectacion: Afectacion;
  readonly familia: string;
  readonly estado: EstadoConcepto;
  readonly origen: OrigenConcepto;
  readonly clonado_de: number | null;
  readonly modo_calculo: ModoCalculo;
  /** Calculo del motor (ISR, IMSS…) cuando `modo_calculo` es SISTEMA. */
  readonly sistema_id: string | null;
  readonly clases: readonly ClaseNomina[];
  readonly captura: readonly DatoCaptura[];
  readonly reglas: Readonly<Partial<Record<TipoRegla, Regla>>>;
  readonly prioridad_descuento: number | null;
  readonly espejo: number | null;
  readonly suma_en: number | null;
  readonly suma_prevision_social: boolean;
  readonly informativo: boolean;
  /**
   * Percepción pactada en neto (etapa C4): lo capturado es lo que recibe libre el trabajador y el cálculo busca el
   * bruto. La API solo lo manda cuando es `true`; al guardar se manda `true` o `false`.
   */
  readonly pactado_en_neto?: boolean;
  readonly grupo_tope_exencion: string | null;
  readonly periodos: PeriodosAplica;
  readonly formas_registro: readonly FormaRegistro[];
  /** Solo deducciones: qué tope de la LFT le aplica el cálculo al descontarla (null: sin clase). */
  readonly limite_lft?: LimiteLft | null;
  readonly orden_recibo: number | null;
  readonly clave_sat: string | null;
  readonly notas: string;
  readonly version: number;
}

/** Lo que se manda en `POST /conceptos`: el servidor pone la version y lo demas por defecto. */
export type ConceptoNuevo = Pick<
  ConceptoFicha,
  | 'clave'
  | 'nombre'
  | 'nombre_corto'
  | 'tipo'
  | 'afectacion'
  | 'familia'
  | 'estado'
  | 'origen'
  | 'modo_calculo'
  | 'clases'
  | 'captura'
  | 'reglas'
  | 'notas'
>;

// ── Ajustes por nivel ───────────────────────────────────────────────────────

/** Alcance tal como lo guarda la API: las dimensiones que no aplican llegan en `null`. */
export interface AlcanceGuardado {
  readonly empresa_id: string | null;
  readonly cliente_id: string | null;
  readonly tipo_nomina_id: string | null;
  readonly esquema_id: string | null;
}

/** Valor del menu de una regla (`{valor}` o `{formula}`) guardado en un ajuste `criterio.<TIPO>`. */
export interface ValorDirecto {
  readonly valor: string | null;
  readonly formula: string | null;
  readonly fundamento: string;
  readonly etiqueta: Etiqueta;
}

/**
 * `estado`, `periodos` e `isn_estado.X`: texto; `nombre_visible`: texto; `prioridad_descuento`:
 * entero o null; `criterio.<TIPO>`: el id de un criterio de la ficha o un valor directo.
 */
export type ValorAjuste = string | number | null | ValorDirecto;

export interface AjusteConcepto {
  readonly id: number;
  readonly alcance: AlcanceGuardado;
  readonly objetivo_tipo: 'CONCEPTO' | 'VARIABLE';
  readonly objetivo_id: string;
  /** estado, nombre_visible, prioridad_descuento, periodos, criterio.<TIPO> o isn_estado.<EDO>. */
  readonly campo: string;
  readonly valor: ValorAjuste;
  readonly motivo: string;
  readonly usuario: string;
  readonly fecha: string;
  readonly activo: boolean;
  readonly version: number;
}

// ── Configuracion efectiva en un contexto ───────────────────────────────────

export interface Traza {
  readonly nivel: NivelAjuste;
  readonly ajuste_id: number | null;
}

export interface CriterioAplicado {
  readonly criterio: Criterio;
  /** La referencia de «distinto a la ley»; null si la regla no tiene criterio de ley. */
  readonly criterio_ley: Criterio | null;
  readonly nivel: NivelAjuste;
  readonly ajuste_id: number | null;
}

export interface ConceptoResuelto {
  /** La ficha con los ajustes del contexto ya aplicados. */
  readonly concepto: ConceptoFicha;
  readonly nombre_visible: string;
  readonly criterios_aplicados: Readonly<Partial<Record<TipoRegla, CriterioAplicado>>>;
  /** Por campo (`estado`, `nombre_visible`, `prioridad_descuento`, `periodos`, `criterio.X`, `isn_estado.X`). */
  readonly trazas: Readonly<Record<string, Traza>>;
}

/** `GET /conceptos/{clave}/configuracion?contexto`. */
export interface ConfiguracionConcepto {
  readonly ficha: ConceptoFicha;
  readonly resuelto: ConceptoResuelto;
  readonly difiere_de_ley: Readonly<Partial<Record<TipoRegla, boolean>>>;
  /** Los ajustes que aplican en el contexto (de este nivel y de los superiores). */
  readonly ajustes: readonly AjusteConcepto[];
}

// ── Lista ───────────────────────────────────────────────────────────────────

export interface ReglaResumen {
  readonly valor: string | null;
  readonly formula: string | null;
  readonly etiqueta: Etiqueta | null;
  readonly nivel: NivelAjuste;
  readonly difiere_de_ley: boolean;
  readonly por_estado: Readonly<Record<string, string>>;
}

/** Cada elemento de `GET /conceptos/resumen?contexto`. */
export interface ResumenConcepto {
  readonly clave: number;
  readonly nombre: string;
  readonly nombre_visible: string;
  readonly tipo: TipoConcepto;
  readonly afectacion: Afectacion;
  readonly familia: string;
  readonly modo_calculo: ModoCalculo;
  readonly origen: OrigenConcepto;
  readonly clases: readonly ClaseNomina[];
  readonly informativo: boolean;
  /** Estado efectivo en el contexto. */
  readonly estado: EstadoConcepto;
  /** Ajustes de este nivel o de uno superior. */
  readonly ajustes_en_contexto: number;
  readonly reglas: Readonly<Partial<Record<TipoRegla, ReglaResumen>>>;
  /** Si los ajustes no corresponden a la ficha, el mensaje de la API. */
  readonly error: string | null;
}

// ── Orden de calculo ────────────────────────────────────────────────────────

export interface PasoOrden {
  readonly etapa: number;
  readonly clave: number;
  readonly depende_de: readonly string[];
  readonly motivo_etapa: string;
}

/** `GET /orden?contexto&clase&periodicidad`. */
export interface RespuestaOrden {
  readonly huella: string;
  readonly orden: readonly PasoOrden[];
  /** La secuencia en texto, agrupada por etapa. */
  readonly explicacion: string;
}

// ── Historial ───────────────────────────────────────────────────────────────

/** Los registros de ajustes traen el alcance donde se hicieron. */
export type RegistroHistorialConAlcance = RegistroHistorial & { readonly alcance?: AlcanceGuardado | null };

// ── Textos en lenguaje sencillo ─────────────────────────────────────────────

export interface TextoRegla {
  readonly pregunta: string;
  readonly ayuda: string;
  readonly opciones: Readonly<Record<string, string>>;
  /** Texto de la opcion con formula; null si la regla no la admite. */
  readonly formula: string | null;
  readonly ayudaFormula: string;
}

/** Las cuatro preguntas fiscales, en el orden en que se muestran (solo percepciones). */
export const REGLAS: Readonly<Record<TipoReglaFiscal, TextoRegla>> = {
  EXENCION_ISR: {
    pregunta: '¿Paga ISR?',
    ayuda: 'Qué parte del pago queda exenta del impuesto sobre la renta.',
    opciones: { GRAVADO: 'Sí, todo paga ISR', EXENTO: 'No, todo es exento' },
    formula: 'Solo una parte',
    ayudaFormula: 'Escribe la fórmula del monto exento. MONTO es el importe del concepto.',
  },
  METODO_ISR: {
    pregunta: '¿Cómo se calcula su ISR?',
    ayuda: 'Método con el que el motor calcula el impuesto de este pago.',
    opciones: {
      ORDINARIO: 'Tarifa del periodo (Art. 96)',
      ART174: 'Tasa efectiva (Art. 174 RLISR)',
      SEPARACION: 'Pago por separación (Art. 95)',
      SEPARACION_SIN_EXENCION: 'Pago por separación, sin la exención por años de servicio (Art. 95)',
      NO_APLICA: 'No aplica',
    },
    formula: null,
    ayudaFormula: '',
  },
  INTEGRACION_SBC: {
    pregunta: '¿Integra al salario base de cotización (IMSS)?',
    ayuda:
      'Si el pago forma parte del SBC para las cuotas del IMSS. El elemento variable entra al promedio del bimestre del IMSS.',
    opciones: {
      INTEGRA: 'Sí, integra (elemento variable)',
      INTEGRA_FIJO: 'Sí, integra (elemento fijo)',
      NO_INTEGRA: 'No integra',
    },
    formula: 'Solo una parte',
    ayudaFormula:
      'Escribe la fórmula del monto que integra (cuenta como elemento variable). MONTO es el importe del concepto.',
  },
  BASE_ISN: {
    pregunta: '¿Es base del impuesto sobre nómina?',
    ayuda: 'Si el pago cuenta para el ISN del estado donde trabaja la persona.',
    opciones: { SI: 'Sí, es base', NO: 'No es base' },
    formula: 'Depende (fórmula)',
    ayudaFormula: 'Escribe el monto que es base o una condición. MONTO es el importe del concepto.',
  },
};

export const TIPOS_FISCALES: readonly TipoReglaFiscal[] = ['EXENCION_ISR', 'METODO_ISR', 'INTEGRACION_SBC', 'BASE_ISN'];

export const ETAPAS: Readonly<Record<number, string>> = {
  1: 'Datos base',
  2: 'Percepciones',
  3: 'Bases',
  4: 'Deducciones de ley',
  5: 'Deducciones sobre resultado',
  6: 'Otras deducciones',
  7: 'Neto',
  8: 'Informativos',
};

export const CLASES: readonly ClaseNomina[] = ['ORDINARIA', 'EXTRAORDINARIA', 'AGUINALDO', 'FINIQUITO', 'COMPLEMENTARIA'];

export const PERIODICIDADES_ORDEN: readonly string[] = ['SEMANAL', 'CATORCENAL', 'QUINCENAL', 'MENSUAL'];

export const AFECTACION: Readonly<Record<Afectacion, string>> = {
  DIRECTA: 'Directa: va en el recibo y en el cálculo',
  INDIRECTA: 'Indirecta: se calcula pero no va en el recibo',
  COMPLEMENTARIA: 'Complementaria: dato que complementa el cálculo',
};

/** Textos cortos de la afectacion para el alta guiada. */
export const AFECTACION_ALTA: Readonly<Record<Afectacion, string>> = {
  DIRECTA: 'Va en el recibo y en el cálculo',
  INDIRECTA: 'Se calcula pero no va en el recibo',
  COMPLEMENTARIA: 'Complementa el cálculo',
};

export const MODOS: Readonly<Record<ModoCalculo, string>> = {
  FORMULA: 'Fórmula',
  CAPTURA: 'Captura',
  SISTEMA: 'Motor',
};

export const MODOS_LARGOS: Readonly<Record<ModoCalculo, string>> = {
  FORMULA: 'Se calcula',
  CAPTURA: 'Se captura',
  SISTEMA: 'Lo calcula el motor',
};

export const TIPOS_CAPTURA: Readonly<Record<Exclude<TipoCampoCaptura, 'BASE'>, string>> = {
  NUMERO: 'Número',
  PORCENTAJE: 'Porcentaje',
  TEXTO: 'Texto',
  OPCION: 'Lista de opciones',
};

export const ESTADOS_MX: readonly string[] = [
  'AGS', 'BC', 'BCS', 'CAMP', 'CHIS', 'CHIH', 'CDMX', 'COAH', 'COL', 'DGO', 'GRO', 'GTO', 'HGO', 'JAL',
  'MEX', 'MICH', 'MOR', 'NAY', 'NL', 'OAX', 'PUE', 'QRO', 'QROO', 'SLP', 'SIN', 'SON', 'TAB', 'TAMS', 'TLAX',
  'VER', 'YUC', 'ZAC',
];

export interface TextoEtiqueta {
  readonly texto: string;
  readonly ayuda: string;
  /** Color de `nz-tag`. */
  readonly color: string;
}

export const ETIQUETAS: Readonly<Record<Etiqueta, TextoEtiqueta>> = {
  LEY: { texto: 'LEY', ayuda: 'Criterio de ley', color: 'blue' },
  CLIENTE: { texto: 'POLÍTICA', ayuda: 'Convención o política', color: 'purple' },
  DERIVADA: { texto: 'POR CONFIRMAR', ayuda: 'Criterio inferido: revísalo', color: 'gold' },
};

export const PERIODOS_APLICA: Readonly<Record<PeriodosAplica, string>> = {
  TODOS: 'En todos',
  PRIMERO_DEL_MES: 'Solo el primero del mes',
  ULTIMO_DEL_MES: 'Solo el último del mes',
};

export interface TextoForma {
  readonly texto: string;
  readonly ayuda: string;
}

/** En el orden en que las guarda el servidor. */
export const FORMAS_REGISTRO: Readonly<Record<FormaRegistro, TextoForma>> = {
  UNA_VEZ: { texto: 'Una vez', ayuda: 'En una exhibición, en el periodo que se indique.' },
  MONTO_HASTA_TOTAL: {
    texto: 'Monto fijo hasta un total',
    ayuda:
      'Cada periodo, desde el que se indique, hasta completar el total; el último descuenta lo que reste. ' +
      'Si el neto no alcanza, lo que falte se queda en el saldo y el plazo se alarga.',
  },
  POR_FECHAS: {
    texto: 'Se repite por fechas',
    ayuda: 'Lo capturado (un monto o un porcentaje) se repite de un periodo a otro, o hasta que se cancele.',
  },
};

/** Acciones de la bitacora de un concepto. */
export const ACCIONES_CONCEPTO: Readonly<Record<string, string>> = {
  IMPORTAR: 'Carga inicial',
  ALTA: 'Alta',
  ACTUALIZAR: 'Cambio',
  CONFIGURAR_REGLA: 'Regla configurada',
  RECONFIGURAR_LEY: 'Criterio de ley reconfigurado',
  AGREGAR_CRITERIO: 'Criterio agregado',
  CLONAR: 'Clonado',
  TRAER: 'Traído de otra',
  RETIRAR: 'Retirado',
  REEMPLAZAR: 'Reemplazado',
};

/** Campos de la ficha, para el resumen de lo que se va a guardar. */
export const NOMBRES_FICHA: Readonly<Record<string, string>> = {
  estado: '¿se usa?',
  prioridad_descuento: 'prioridad de descuento',
  nombre: 'nombre',
  nombre_corto: 'nombre corto',
  afectacion: 'afectación',
  familia: 'familia',
  orden_recibo: 'orden en el recibo',
  clases: 'nóminas donde aplica',
  suma_prevision_social: 'previsión social',
  informativo: 'informativo',
  pactado_en_neto: 'se pacta en neto',
  grupo_tope_exencion: 'grupo de tope',
  notas: 'notas',
  captura: 'datos que se capturan',
  isn: 'excepciones del ISN',
  modo_calculo: 'cómo se obtiene el monto',
  periodos: 'en qué periodos se aplica',
  formas_registro: 'cómo se registra a cada trabajador',
  limite_lft: 'límite de descuento (LFT)',
};

// ── Funciones de texto ──────────────────────────────────────────────────────

/** `CLIENTE_EMPRESA` → `Cliente empresa`. */
export function titulo(t: string | null | undefined): string {
  return t ? t.charAt(0) + t.slice(1).toLowerCase().replace(/_/g, ' ') : '';
}

/** Un criterio (o un valor pendiente) dicho como en las opciones de la pregunta. */
export function textoCriterio(
  tipo: TipoRegla,
  criterio: { readonly valor?: string | number | null; readonly formula?: string | null } | null | undefined,
): string {
  if (!criterio) return 'sin regla';
  if (criterio.formula) return `fórmula: ${criterio.formula}`;
  const opciones: Readonly<Record<string, string>> = tipo === 'CALCULO' ? {} : REGLAS[tipo].opciones;
  const valor = criterio.valor === null || criterio.valor === undefined ? '' : String(criterio.valor);
  return opciones[valor] ?? (valor || '—');
}

/** Nombre de un campo ajustable en lenguaje sencillo. */
export function nombreCampo(campo: string): string {
  if (campo === 'estado') return '¿Se usa?';
  if (campo === 'nombre_visible') return 'Nombre visible';
  if (campo === 'prioridad_descuento') return 'Prioridad de descuento';
  if (campo === 'periodos') return 'En qué periodos se aplica';
  if (campo === 'criterio.CALCULO' || campo === 'modo_calculo') return 'Fórmula del monto';
  if (campo.startsWith('criterio.')) {
    const tipo = campo.slice(9);
    return esTipoFiscal(tipo) ? REGLAS[tipo].pregunta : campo;
  }
  if (campo.startsWith('isn_estado.')) return `ISN en ${campo.split('.')[1]}`;
  return campo;
}

export function esTipoFiscal(tipo: string): tipo is TipoReglaFiscal {
  return (TIPOS_FISCALES as readonly string[]).includes(tipo);
}

/** Alcance guardado (con nulls) → alcance de la pantalla (sin las dimensiones vacias). */
export function alcanceDe(a: AlcanceGuardado | null | undefined): Alcance {
  const r: { esquema_id?: string; empresa_id?: string; cliente_id?: string; tipo_nomina_id?: string } = {};
  if (a?.esquema_id) r.esquema_id = a.esquema_id;
  if (a?.empresa_id) r.empresa_id = a.empresa_id;
  if (a?.cliente_id) r.cliente_id = a.cliente_id;
  if (a?.tipo_nomina_id) r.tipo_nomina_id = a.tipo_nomina_id;
  return r;
}

/** Llave estable de un alcance, para agrupar. */
export function llaveAlcance(a: AlcanceGuardado | null | undefined): string {
  return [a?.esquema_id, a?.empresa_id, a?.cliente_id, a?.tipo_nomina_id].map((d) => d ?? '').join('|');
}
